import { featureFlags } from './featureFlags'

/**.
 * The feature flag is enabled by its configured default or URL query parameter
 * (e.g. ?newBomItemDetails). Flags can require a specific value and opt out
 * of session storage persistence through their featureFlags configuration.
 * @param featureFlags the feature flag to check
 * @returns
 */
export const useFeatureFlag = (
  flagsToCheck: Array<keyof typeof featureFlags>,
) => {
  const searchParams = new URLSearchParams(window.location.search.toLowerCase())

  const enabledFeaturesKey = Object.keys(featureFlags).filter(
    (key) => featureFlags[key].enabled
  )

  if (!enabledFeaturesKey.length) {
    return false
  }

  const featureFlagsEnabled = flagsToCheck.every((flag) => {
    const featureFlag = featureFlags[flag] as {
      enabled: boolean
      enabledByDefaultInDev?: boolean
      queryParameterValue?: string
    }
    const parameterName = flag.toLowerCase()

    if (!featureFlag.enabled) return false

    if (!searchParams.has(parameterName)) {
      return (
        featureFlag.enabledByDefaultInDev === true &&
        (import.meta.env.DEV || import.meta.env.VITE_ENVIRONMENT === 'dev')
      )
    }

    return (
      enabledFeaturesKey.includes(flag) &&
      (!featureFlag.queryParameterValue ||
        searchParams.get(parameterName) === featureFlag.queryParameterValue)
    )
  })

  const persistInSession = flagsToCheck.every(
    (flag) =>
      (featureFlags[flag] as { persistInSession?: boolean }).persistInSession !==
      false
  )

  if (!persistInSession) {
    return featureFlagsEnabled
  }

  try {
    if (featureFlagsEnabled) {
      flagsToCheck.forEach((flag) => {
        sessionStorage.setItem(flag, 'true')
      })
      return true
    } else {
      return flagsToCheck.every((flag) => {
        const sessionStorageFlag = sessionStorage.getItem(flag)
        return sessionStorageFlag === 'true'
      })
    }
  } catch (err) {
    console.warn('useFeatureFlag', err)
    return featureFlagsEnabled
  }
}
